import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import likesApi from '../api/likesApi';
import { Profile, LoadingStatus } from '../../types';

const LS_KEY = 'crays_seen_matches_count';

function getSeenCount(): number {
  const raw = localStorage.getItem(LS_KEY);
  return raw ? parseInt(raw, 10) : 0;
}

interface LikesState {
  likedProfiles: Profile[];
  matches: Profile[];
  status: LoadingStatus;
  seenMatchesCount: number;
}

const initialState: LikesState = {
  likedProfiles: [],
  matches: [],
  status: 'idle',
  seenMatchesCount: getSeenCount(),
};

export const fetchLikedProfiles = createAsyncThunk(
  'likes/fetchLiked',
  async (_, { rejectWithValue }) => {
    try {
      const res = await likesApi.getLikedProfiles();
      return res.data as Profile[];
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load liked profiles');
    }
  },
);

export const fetchMatches = createAsyncThunk(
  'likes/fetchMatches',
  async (_, { rejectWithValue }) => {
    try {
      const res = await likesApi.getMatches();
      return res.data as Profile[];
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load matches');
    }
  },
);

export const likeProfile = createAsyncThunk(
  'likes/like',
  async (userId: string, { rejectWithValue }) => {
    try {
      const res = await likesApi.likeProfile(userId);
      return res.data as { isMatch: boolean };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to like profile');
    }
  },
);

export const unlikeProfile = createAsyncThunk(
  'likes/unlike',
  async (userId: string, { rejectWithValue }) => {
    try {
      await likesApi.unlikeProfile(userId);
      return userId;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to unlike profile');
    }
  },
);

const likesSlice = createSlice({
  name: 'likes',
  initialState,
  reducers: {
    markLikesSeen(state) {
      state.seenMatchesCount = state.matches.length;
      localStorage.setItem(LS_KEY, String(state.matches.length));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLikedProfiles.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchLikedProfiles.fulfilled, (state, { payload }) => {
        state.likedProfiles = payload;
        state.status = 'succeeded';
      })
      .addCase(fetchLikedProfiles.rejected, (state) => { state.status = 'failed'; })
      .addCase(fetchMatches.fulfilled, (state, { payload }) => {
        state.matches = payload;
      });
  },
});

export const { markLikesSeen } = likesSlice.actions;
export default likesSlice.reducer;

export const selectLikedProfiles = (state: { likes: LikesState }) => state.likes.likedProfiles;
export const selectMatches = (state: { likes: LikesState }) => state.likes.matches;
export const selectLikesStatus = (state: { likes: LikesState }) => state.likes.status;
export const selectUnseenLikesCount = (state: { likes: LikesState }) =>
  Math.max(0, state.likes.matches.length - state.likes.seenMatchesCount);
